// src/pages/store-pickup/components/StorePickupPaymentRecordsModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  CheckCircle2,
  XCircle,
  Save,
  Upload,
  Paperclip,
  Trash2,
  Calendar,
  ShieldCheck,
  Lock
} from 'lucide-react';
import { deleteStorePickupPaymentRecord } from '../../../services/api/storePickup';
import { DataTable } from '../../../components/common';
import {
  getPaymentRecords,
  updatePaymentRecord,
  verifyPaymentRecord,
  bulkVerifyPaymentRecords,
  uploadOrderPaymentProof
} from '../../../services/api/paymentVerification';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/currencyUtil';
import { formatDate, formatTime } from '../../../utils/dateUtils';
import { getImageUrl } from '../../../utils/imageUtils';
import { isBankTransferMissingRef } from '../../../utils/paymentRecordUtils';
import { PaymentMethodIcon } from '../../../utils/paymentMethodIcons';
import styles from './StorePickupPaymentRecordsModal.module.css';

const VALID_PROOF_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_PROOF_SIZE = 5 * 1024 * 1024; // 5MB
const isPdfUrl = (url) => !!url && url.toLowerCase().endsWith('.pdf');

const StorePickupPaymentRecordsModal = ({ order, onClose, onVerificationChange }) => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const canVerify = hasPermission('verify_payment_records');
  // Same rule as the POS: only unverified payments can be deleted, verified ones are locked
  const canDelete = hasPermission('dispatch_store_pickup_orders');

  const [paymentRecords, setPaymentRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editBuffers, setEditBuffers] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [proofErrors, setProofErrors] = useState({});

  const fetchPaymentRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getPaymentRecords({ orderId: order.id, limit: 100 });
      setPaymentRecords(response.paymentRecords || []);
      setEditBuffers({});
    } catch (err) {
      console.error('Failed to fetch payment records:', err);
      showError(t('paymentVerification.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  }, [order.id, showError, t]);

  useEffect(() => {
    fetchPaymentRecords();
  }, [fetchPaymentRecords]);

  useEffect(() => {
    if (isLoading) return;
    onVerificationChange?.(order.id, paymentRecords.some((r) => !r.isVerified));
  }, [paymentRecords, isLoading, order.id, onVerificationChange]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const getFieldValue = (record, field) => {
    if (editBuffers[record.id] && editBuffers[record.id][field] !== undefined) {
      return editBuffers[record.id][field];
    }
    return record[field] ?? '';
  };

  const isDirty = (record) => {
    const buffer = editBuffers[record.id];
    if (!buffer) return false;
    return (
      (buffer.reference !== undefined && buffer.reference !== (record.reference || '')) ||
      (buffer.paymentDate !== undefined && buffer.paymentDate !== (record.paymentDate || ''))
    );
  };

  const handleFieldChange = (recordId, field, value) => {
    setEditBuffers((prev) => ({ ...prev, [recordId]: { ...prev[recordId], [field]: value } }));
  };

  const replaceRecordInState = (updatedRecord) => {
    setPaymentRecords((prev) => prev.map((r) => (r.id === updatedRecord.id ? { ...r, ...updatedRecord } : r)));
  };

  const handleSaveEdit = async (record) => {
    const buffer = editBuffers[record.id];
    if (!buffer) return;
    try {
      const payload = {};
      if (buffer.reference !== undefined) payload.reference = buffer.reference;
      if (buffer.paymentDate !== undefined) payload.paymentDate = buffer.paymentDate;
      const response = await updatePaymentRecord(record.id, payload);
      replaceRecordInState(response.paymentRecord);
      setEditBuffers((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
      success(t('paymentVerification.updateSuccess'));
    } catch (err) {
      console.error('Failed to update payment record:', err);
      showError(err.response?.data?.message || t('paymentVerification.updateFailed'));
    }
  };

  const handleProofChange = async (record, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!VALID_PROOF_TYPES.includes(file.type)) {
      setProofErrors((prev) => ({ ...prev, [record.id]: t('ecomOrders.invalidProofFormat') || 'Only .jpg, .png or .pdf files are allowed' }));
      return;
    }
    if (file.size > MAX_PROOF_SIZE) {
      setProofErrors((prev) => ({ ...prev, [record.id]: t('ecomOrders.proofTooLarge') || 'File must be under 5MB' }));
      return;
    }

    setProofErrors((prev) => ({ ...prev, [record.id]: '' }));
    setUploadingId(record.id);
    try {
      const proofUrl = await uploadOrderPaymentProof(file);
      const response = await updatePaymentRecord(record.id, { proofUrl });
      replaceRecordInState(response.paymentRecord);
    } catch (err) {
      console.error('Payment proof upload failed:', err);
      setProofErrors((prev) => ({ ...prev, [record.id]: t('paymentVerification.proofUploadFailed') }));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteProof = async (record) => {
    const confirmed = await confirm(t('paymentVerification.confirmDeleteProof'), {
      title: t('paymentVerification.deleteProof'),
      confirmText: t('paymentVerification.deleteProof'),
      cancelText: t('common.cancel')
    });
    if (!confirmed) return;
    try {
      const response = await updatePaymentRecord(record.id, { proofUrl: null });
      replaceRecordInState(response.paymentRecord);
      success(t('paymentVerification.updateSuccess'));
    } catch (err) {
      console.error('Failed to delete payment proof:', err);
      showError(err.response?.data?.message || t('paymentVerification.updateFailed'));
    }
  };

  const getVerifyBlockReason = (record) => {
    if (isDirty(record)) return t('paymentVerification.saveChangesBeforeVerify');
    if (isBankTransferMissingRef(record)) return t('paymentVerification.referenceRequiredForBankTransfer');
    return null;
  };

  const handleVerifyOne = async (record) => {
    const blockReason = getVerifyBlockReason(record);
    if (blockReason) {
      showError(blockReason);
      return;
    }

    const confirmed = await confirm(t('paymentVerification.confirmVerify'), {
      title: t('paymentVerification.verify'),
      confirmText: t('paymentVerification.verify'),
      cancelText: t('common.cancel')
    });
    if (!confirmed) return;

    try {
      const response = await verifyPaymentRecord(record.id);
      replaceRecordInState(response.paymentRecord);
      setSelectedIds((prev) => prev.filter((id) => id !== record.id));
      success(t('paymentVerification.verifySuccess'));
    } catch (err) {
      console.error('Failed to verify payment record:', err);
      showError(err.response?.data?.message || t('paymentVerification.verifyFailed'));
    }
  };

  const handleDeleteRecord = async (record) => {
    const confirmed = await confirm(t('storePickup.confirmDeletePayment'), {
      title: t('storePickup.deletePayment'),
      confirmText: t('storePickup.deletePayment'),
      cancelText: t('common.cancel')
    });
    if (!confirmed) return;

    try {
      await deleteStorePickupPaymentRecord(order.id, record.id);
      setPaymentRecords((prev) => prev.filter((r) => r.id !== record.id));
      setSelectedIds((prev) => prev.filter((id) => id !== record.id));
      success(t('storePickup.deletePaymentSuccess'));
    } catch (err) {
      console.error('Failed to delete payment record:', err);
      showError(err.response?.data?.message || t('storePickup.deletePaymentFailed'));
    }
  };

  const unverifiedSelectedIds = selectedIds.filter((id) => {
    const record = paymentRecords.find((r) => r.id === id);
    return record && !record.isVerified;
  });

  const handleBulkVerify = async () => {
    if (unverifiedSelectedIds.length === 0) {
      showError(t('paymentVerification.noUnverifiedSelected'));
      return;
    }

    const unsavedCount = unverifiedSelectedIds.filter((id) =>
      isDirty(paymentRecords.find((r) => r.id === id))
    ).length;
    if (unsavedCount > 0) {
      showError(t('paymentVerification.bulkUnsavedChanges', { count: unsavedCount }));
      return;
    }

    const missingRefCount = unverifiedSelectedIds.filter((id) =>
      isBankTransferMissingRef(paymentRecords.find((r) => r.id === id))
    ).length;
    if (missingRefCount > 0) {
      showError(t('paymentVerification.bulkReferenceMissing', { count: missingRefCount }));
      return;
    }
    const confirmed = await confirm(
      t('paymentVerification.confirmBulkVerify', { count: unverifiedSelectedIds.length }),
      {
        title: t('paymentVerification.verifySelected', { count: unverifiedSelectedIds.length }),
        confirmText: t('paymentVerification.verify'),
        cancelText: t('common.cancel')
      }
    );
    if (!confirmed) return;

    try {
      const result = await bulkVerifyPaymentRecords(unverifiedSelectedIds);
      setSelectedIds([]);
      await fetchPaymentRecords();
      if (result.summary.failureCount === 0) {
        success(t('paymentVerification.bulkVerifySuccess', { count: result.summary.successCount }));
      } else {
        showError(
          t('paymentVerification.bulkVerifyPartial', {
            successCount: result.summary.successCount,
            totalSubmitted: result.summary.totalSubmitted,
            failureCount: result.summary.failureCount
          })
        );
      }
    } catch (err) {
      console.error('Bulk verify failed:', err);
      showError(err.response?.data?.message || t('paymentVerification.bulkVerifyFailed'));
    }
  };

  const columns = [
    {
      key: 'paymentMethod',
      header: t('paymentVerification.paymentMethod'),
      render: (record) => (
        <div className={styles.methodCell}>
          <PaymentMethodIcon iconFileName={record.PaymentMethod?.iconFileName} label={record.PaymentMethod?.label} />
        </div>
      )
    },
    {
      key: 'paymentType',
      header: t('paymentVerification.paymentType'),
      width: '90px',
      render: (record) => (
        <span className={styles.paymentTypeBadge}>
          {record.EcomOrder?.PaymentType?.key?.toUpperCase() || '-'}
        </span>
      )
    },
    {
      key: 'paymentAmount',
      header: t('paymentVerification.amount'),
      align: 'right',
      width: '110px',
      render: (record) => formatCurrency(record.paymentAmount)
    },
    {
      key: 'reference',
      header: t('paymentVerification.reference'),
      width: '160px',
      render: (record) => (
        <input
          type="text"
          className={styles.inlineInput}
          value={getFieldValue(record, 'reference')}
          disabled={record.isVerified || !canVerify}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleFieldChange(record.id, 'reference', e.target.value)}
        />
      )
    },
    {
      key: 'paymentDate',
      header: t('paymentVerification.paymentDate'),
      width: '150px',
      render: (record) => (
        <input
          type="date"
          className={styles.inlineInput}
          value={getFieldValue(record, 'paymentDate')}
          disabled={record.isVerified || !canVerify}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleFieldChange(record.id, 'paymentDate', e.target.value)}
        />
      )
    },
    {
      key: 'proof',
      header: t('paymentVerification.proof'),
      width: '160px',
      render: (record) => (
        <div className={styles.proofCell} onClick={(e) => e.stopPropagation()}>
          {record.proofUrl ? (
            <>
              {isPdfUrl(record.proofUrl) ? (
                <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofFileLink}>
                  <Paperclip size={14} />
                  {t('paymentVerification.viewProof')}
                </a>
              ) : (
                <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofThumbnailLink}>
                  <img src={getImageUrl(record.proofUrl)} alt="Payment proof" className={styles.proofThumbnail} />
                </a>
              )}
              {!record.isVerified && canVerify && (
                <div className={styles.proofActions}>
                  <button
                    type="button"
                    className={styles.proofIconButton}
                    title={t('paymentVerification.changeProof')}
                    onClick={() => document.getElementById(`spProofInput-${record.id}`).click()}
                  >
                    <Upload size={14} />
                  </button>
                  <button
                    type="button"
                    className={styles.proofIconButton}
                    title={t('paymentVerification.deleteProof')}
                    onClick={() => handleDeleteProof(record)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </>
          ) : record.isVerified || !canVerify ? (
            <span className={styles.noProofText}>{t('paymentVerification.noFileChosen')}</span>
          ) : (
            <button
              type="button"
              className={styles.uploadProofButton}
              onClick={() => document.getElementById(`spProofInput-${record.id}`).click()}
              disabled={uploadingId === record.id}
            >
              <Upload size={14} />
              {uploadingId === record.id ? (t('common.uploading') || 'Uploading...') : t('paymentVerification.noFileChosen')}
            </button>
          )}
          {canVerify && (
            <input
              id={`spProofInput-${record.id}`}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleProofChange(record, e)}
            />
          )}
          {proofErrors[record.id] && <span className={styles.errorText}>{proofErrors[record.id]}</span>}
        </div>
      )
    },
    {
      key: 'isVerified',
      header: t('paymentVerification.isVerified'),
      width: '130px',
      render: (record) =>
        record.isVerified ? (
          <span className={`${styles.statusBadge} ${styles.verifiedBadge}`}>
            <CheckCircle2 size={14} />
            {t('paymentVerification.verified')}
          </span>
        ) : (
          <span className={`${styles.statusBadge} ${styles.unverifiedBadge}`}>
            <XCircle size={14} />
            {t('paymentVerification.unverified')}
          </span>
        )
    },
    {
      key: 'createdByName',
      header: t('paymentVerification.createdBy'),
      render: (record) => record.createdBy?.name || '-'
    },
    {
      key: 'createdAt',
      header: t('paymentVerification.createdDate'),
      width: '130px',
      render: (record) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}><Calendar size={12} />{formatDate(record.createdAt)}</div>
          <div className={styles.timeInfo}>{formatTime(record.createdAt)}</div>
        </div>
      )
    },
    {
      key: 'verifiedByName',
      header: t('paymentVerification.verifiedBy'),
      render: (record) => record.verifiedBy?.name || '-'
    },
    {
      key: 'verifiedAt',
      header: t('paymentVerification.verifiedDate'),
      width: '130px',
      render: (record) =>
        record.isVerified && record.verifiedAt ? (
          <div className={styles.dateCell}>
            <div className={styles.dateInfo}><Calendar size={12} />{formatDate(record.verifiedAt)}</div>
            <div className={styles.timeInfo}>{formatTime(record.verifiedAt)}</div>
          </div>
        ) : '-'
    }
  ];

  const tableActions = (canVerify || canDelete) ? {
    width: '120px',
    frozen: true,
    render: (record) =>
      record.isVerified ? (
        <span className={styles.lockedIcon} title={t('storePickup.verifiedLocked')}>
          <Lock size={14} />
        </span>
      ) : (
        <div className={styles.actionsWrapper}>
          {canVerify && isDirty(record) && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.saveButton}`}
              title={t('paymentVerification.save')}
              onClick={() => handleSaveEdit(record)}
            >
              <Save size={14} />
            </button>
          )}
          {canVerify && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.verifyButton}`}
              title={getVerifyBlockReason(record) || t('paymentVerification.verify')}
              disabled={!!getVerifyBlockReason(record)}
              onClick={() => handleVerifyOne(record)}
            >
              <CheckCircle2 size={14} />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.deleteButton}`}
              title={t('storePickup.deletePayment')}
              onClick={() => handleDeleteRecord(record)}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )
  } : undefined;

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <ShieldCheck size={24} />
              <div>
                <h2>{t('storePickup.paymentRecords')}</h2>
                <p className={styles.orderNumber}>#{order.orderNumber}</p>
              </div>
            </div>
            <button onClick={onClose} className={styles.closeButton}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={styles.modalBody}>
          {canVerify && selectedIds.length > 0 && (
            <div className={styles.bulkActionsBar}>
              <span className={styles.selectedCount}>{selectedIds.length} {t('common.selected') || 'selected'}</span>
              <div className={styles.bulkActionsRight}>
                <button
                  onClick={handleBulkVerify}
                  className={styles.bulkActionButton}
                  disabled={unverifiedSelectedIds.length === 0}
                >
                  <CheckCircle2 size={15} />
                  {t('paymentVerification.verifySelected', { count: unverifiedSelectedIds.length })}
                </button>
                <button onClick={() => setSelectedIds([])} className={styles.bulkCancelButton}>
                  {t('paymentVerification.clearSelection')}
                </button>
              </div>
            </div>
          )}

          <DataTable
            data={paymentRecords}
            columns={columns}
            actions={tableActions}
            selectable={canVerify}
            selectedRows={selectedIds}
            onSelectionChange={setSelectedIds}
            loading={isLoading}
            emptyMessage={t('paymentVerification.noRecordsFound')}
          />
        </div>
      </div>
    </div>
  );
};

export default StorePickupPaymentRecordsModal;
